import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { Refresh as RefreshIcon, Print as PrintIcon } from '@material-ui/icons';
import { FHETable } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';
import { getBoxes } from '../../data/Boxes/actions';
import getSelectors from '../Home/selectors';

class Box extends Component {
    static propTypes = {
        codeEnv: PropTypes.number.isRequired,
        getBoxes: PropTypes.func.isRequired,
        Boxes: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        profile: PropTypes.object.isRequired,
        initialized: PropTypes.bool.isRequired
    };

    constructor(props) {
        super(props);
        const { initialized } = props;
        this.state = {
            loading: !initialized
        };
    }

    componentDidMount() {
        const { initialized } = this.props;
        if (!initialized) {
            this.refreshBoxes();
        }
    }

    refreshBoxes = () => {
        const { getBoxes } = this.props;
        this.setState({ loading: true }, () => {
            return getBoxes()
                .catch(err => {
                    const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                    Toastr.error(message);
                })
                .finally(() => {
                    this.setState({ loading: false });
                });
        });
    };

    hasRightToPrint = () => {
        const { profile, history } = this.props;

        return profile.get('admin') || profile.getIn(['user_rights', 'testbench'], []).includes(`boxes_${history.environment}`);
    };

    renderActions = memoizeOne(() => {
        const { history, codeEnv } = this.props;
        const actions = {
            row: [],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate(
                        `generic.center.table.globalAction.refresh.tooltip`,
                        Translator.translate(`${history.environment}.centerName`)
                    ),
                    isFreeAction: true,
                    onClick: this.refreshBoxes
                }
            ]
        };

        if (this.hasRightToPrint()) {
            actions.row.push({
                icon: PrintIcon,
                tooltip: Translator.translate('testbench.buttons.printIcon'),
                onClick: ({ boxId, macAddress, codePack }) => {
                    tools.printLabel({ codePack, codeEnv, boxId, envBox: history.environment, boxMac: macAddress });
                }
            });
        }

        return actions;
    });

    renderData = memoizeOne(Boxes => {
        let result = [];

        Boxes.map((Box, macAddress) => {
            result.push({
                boxId: parseInt(Box.get('boxId')),
                homeId: Box.get('homeId') ? parseInt(Box.get('homeId')) : null,
                macAddress: macAddress.match(/.{1,2}/g).join(':'),
                firmwareVersion: Box.get('firmwareVersion'),
                boxIsConnected: Box.get('boxIsConnected'),
                state:
                    Box.get('boxIsConnected') === null
                        ? null
                        : Box.get('boxIsConnected')
                        ? Translator.translate('general.connected')
                        : Translator.translate('general.disconnected'),
                boxState: Box.get('boxState') && Translator.translate(`general.${Box.get('boxState')}`),
                provisionedDate: Box.get('provisionedDate'),
                codePack: Box.get('codePack')
            });
        });
        return result;
    });

    renderColumns = memoizeOne(() => {
        const { classes, history } = this.props;

        return [
            {
                title: Translator.translate(`general.table.column.boxId`, Translator.translate(`${history.environment}.centerName`)),
                name: 'boxId',
                defaultSort: 'asc',
                width: 90,
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.homeId'),
                name: 'homeId',
                width: 90,
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.macAddress'),
                name: 'macAddress',
                width: 150,
                customFilter: (value, filter, row) => {
                    if (!value) return false;
                    const formattedValue = value.toLowerCase().replace(/:/g, '');
                    const formattedFilter = filter.value.toLowerCase().replace(/:/g, '');
                    return formattedValue.indexOf(formattedFilter) !== -1;
                }
            },
            {
                title: Translator.translate('general.table.column.firmwareVersion'),
                name: 'firmwareVersion'
            },
            {
                title: Translator.translate('general.table.column.boxState'),
                name: 'boxState'
            },
            {
                title: Translator.translate('general.table.column.state'),
                name: 'state',
                render: (row, data, value) => {
                    if (!value) return null;
                    return (
                        <span className={value === Translator.translate('general.connected') ? classes.connected : classes.disconnected}>
                            {value}
                        </span>
                    );
                }
            },
            {
                title: Translator.translate('general.table.column.provisionedDate'),
                name: 'provisionedDate'
            }
        ];
    });

    render() {
        const { loading } = this.state;
        const { history, Boxes } = this.props;

        return (
            <FHETable
                idTable="boxesList"
                columns={this.renderColumns()}
                data={this.renderData(Boxes)}
                actions={this.renderActions()}
                title={Translator.translate(`generic.center.table.title`, Translator.translate(`${history.environment}.centerName`))}
                emptyDataSourceMessage={Translator.translate(
                    `generic.center.table.body.emptyDataSourceMessage`,
                    Translator.translate(`${history.environment}.centerName`)
                )}
                isLoading={loading}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const { history } = props;
    const selectors = getSelectors(history.environment);
    let Boxes = state.environments[history.environment].data.Boxes.get('list').filter(Box => {
        return Box.get('homeId') === null || ['nouvelle', 'inactive'].indexOf(Box.get('boxState')) !== -1;
    });

    return {
        profile: state.data.user,
        Boxes,
        initialized: state.environments[history.environment].data.Boxes.get('initialized'),
        codeEnv: selectors.getCodeEnv(state)
    };
}

const styles = theme => ({
    connected: {
        color: theme.palette.success.main
    },
    disconnected: {
        color: theme.palette.error.main
    }
});

export default withRouter(
    renderEnvComponent(
        'scenes/Box/index',
        connect(
            mapStateToProps,
            (dispatch, ownProps) => bindActionCreators(tools.mergePropsToAction(ownProps.history.environment, { getBoxes }), dispatch)
        )(withStyles(styles, { withTheme: true })(Box))
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Box/index.js